import React, { useMemo, useState } from "react";
import { ChevronRight, X } from "lucide-react";

type FaqItem = {
  question: string;
  answer: string;
};

type OpenState = {
  idx: number;
  isLarge: boolean;
} | null;

const FAQSection: React.FC = () => {
  const faqs: FaqItem[] = useMemo(
    () => [
      {
        question: "¿Cuánto tiempo toma completar un proyecto?",
        answer:
          "El tiempo de desarrollo varía según la complejidad del proyecto. Proyectos simples pueden tomar de 4 a 6 semanas, mientras que proyectos más complejos pueden tomar de 12 a 16 semanas. Realizamos una evaluación inicial para proporcionarte un cronograma preciso.",
      },
      {
        question: "¿Qué métodos de pago aceptan?",
        answer:
          "Aceptamos transferencias bancarias o depósitos utilizando la facilidad que nos ofrecen la Banca Movil. Aplicamos un sistema de pagos escalonados: 40% al inicio, 60% al entregar.",
      },
      {
        question: "¿Proporcionan mantenimiento después del lanzamiento?",
        answer:
          "Sí, ofrecemos un plan gratuito de mantenimiento post-lanzamiento que incluyen actualizaciones, soporte técnico, monitoreo y corrección de errores por un tiempo limitado después de la fecha de entrega.",
      },
      {
        question: "¿Trabajan con clientes internacionales?",
        answer:
          "Absolutamente. Trabajamos con clientes de todo el mundo. Nuestro equipo está distribuido en diferentes zonas horarias, lo que nos permite ofrecer soporte continuo y comunicación fluida sin importar tu ubicación.",
      },
      {
        question: "¿Cómo manejan la propiedad intelectual?",
        answer:
          "Una vez que el proyecto está completo y pagado en su totalidad, todos los derechos de propiedad intelectual se transfieren al cliente. Utilizamos contratos claros que especifican la transferencia de derechos.",
      },
      {
        question: "¿Y si necesito cambios después de finalizado el proyecto?",
        answer:
          "Ofrecemos un período de gracia de 15 días para correcciones menores después del lanzamiento. Para cambios mayores o nuevas funcionalidades, creamos una propuesta separada con timeline y presupuesto definidos.",
      },
      {
        question: "¿Cómo garantizan la calidad del software?",
        answer:
          "Implementamos pruebas rigurosas en cada etapa del desarrollo, incluyendo pruebas unitarias, de integración y de usuario. Además, seguimos las mejores prácticas de codificación y realizamos revisiones de código para asegurar la calidad.",
      },
      {
        question: "¿Puedo seguir el progreso de mi proyecto?",
        answer:
          "Sí, utilizamos herramientas de gestión de proyectos como Jira o Trello que te permiten ver el progreso en tiempo real. Además, programamos reuniones regulares para mantenerte informado y recibir tu feedback.",
      },
      {
        question: "¿Qué tecnologías utilizan?",
        answer:
          "Utilizamos una amplia gama de tecnologías modernas. La elección de la tecnología depende de los requisitos específicos de cada proyecto para garantizar el mejor rendimiento y escalabilidad.",
      },
      {
        question: "¿Ofrecen capacitación para el uso del software?",
        answer:
          "Sí, proporcionamos sesiones de capacitación para tu equipo, ya sea de forma presencial o virtual. También entregamos documentación detallada para facilitar el uso y mantenimiento del software.",
      },
      {
        question: "¿Cómo manejan la seguridad de los datos?",
        answer:
          "Implementamos medidas de seguridad robustas, incluyendo cifrado de datos, autenticación segura y auditorías regulares. Cumplimos con las normativas de protección de datos aplicables para garantizar la seguridad de tu información.",
      },
      {
        question: "¿Pueden integrarse con sistemas existentes?",
        answer:
          "Sí, tenemos experiencia en integrar nuevas soluciones con sistemas existentes mediante APIs y otros métodos de integración. Evaluamos tus sistemas actuales para asegurar una integración fluida y eficiente.",
      },
    ],
    [],
  );

  const [open, setOpen] = useState<OpenState>(null);

  const closeFaq = () => setOpen(null);

  const activeFaq = open ? faqs[open.idx] : null;

  const rows = useMemo(() => {
    const out: Array<{ left: number; right?: number }> = [];
    for (let i = 0; i < faqs.length; i += 2) {
      out.push({ left: i, right: i + 1 < faqs.length ? i + 1 : undefined });
    }
    return out;
  }, [faqs.length]);

  const borderLR =
    "linear-gradient(90deg, #7F5600 0%, #463002 23%, #261A00 100%)";
  const borderRL =
    "linear-gradient(270deg, #7F5600 0%, #463002 23%, #261A00 100%)";

  const FaqCard: React.FC<{
    idx: number;
    wClass: string;
    isLarge: boolean;
    borderDir: "lr" | "rl";
  }> = ({ idx, wClass, isLarge, borderDir }) => {
    const baseGradient = isLarge
      ? "bg-[linear-gradient(90deg,#000000_0%,#0F0A00_55%,#201600_100%)]"
      : "bg-[linear-gradient(90deg,#201600_0%,#0F0A00_45%,#000000_100%)]";

    const activeGradient = isLarge
      ? "bg-[linear-gradient(90deg,#050505_0%,#2F2000_48%,#A96F00_78%,#D08E00_100%)]"
      : "bg-[linear-gradient(90deg,#D08E00_0%,#A96F00_22%,#2F2000_52%,#050505_100%)]";

    return (
      <div
        className={`group w-full rounded-2xl p-[1px] self-start h-fit ${wClass} md:flex-none transition-all duration-500 ease-out hover:scale-[1.015] hover:shadow-[0_0_12px_rgba(208,142,0,0.45),0_0_28px_rgba(208,142,0,0.25),0_0_55px_rgba(127,86,0,0.15)]`}
        style={{
          background: borderDir === "lr" ? borderLR : borderRL,
        }}
      >
        <button
          type="button"
          onClick={() => setOpen({ idx, isLarge })}
          className={`relative overflow-hidden text-left rounded-2xl p-6 cursor-pointer transition-all duration-500 ease-out flex items-center justify-between gap-4 w-full ${baseGradient}`}
          aria-haspopup="dialog"
          aria-expanded={open?.idx === idx}
        >
          {/* Gradiente de encendido */}
          <div
            className={`absolute inset-0 ${activeGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-out pointer-events-none`}
          />

          {/* Luz interna */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-[radial-gradient(circle_at_center,rgba(255,193,61,0.18),transparent_68%)] transition-opacity duration-700 ease-out pointer-events-none" />

          <h3 className="relative z-10 text-sm sm:text-base md:text-lg font-semibold text-white leading-snug transition-all duration-500 group-hover:text-white group-hover:drop-shadow-[0_0_8px_rgba(255,193,61,0.35)]">
            {faqs[idx].question}
          </h3>

          <ChevronRight className="relative z-10 shrink-0 h-5 w-5 text-white/90 transition-all duration-500 ease-out group-hover:text-[#FFD36A] group-hover:translate-x-1 group-hover:drop-shadow-[0_0_6px_rgba(255,193,61,0.7)]" />
        </button>
      </div>
    );
  };

  return (
    <section
      id="questions"
      className="min-h-screen bg-black text-white py-16 px-4 sm:px-6 lg:px-20 relative overflow-hidden"
    >
      {/* Título */}
      <div className="text-center mb-16 relative z-40">
        <h2 className="text-xl md:text-2xl font-bold tracking-[4px] uppercase mb-4">
          PREGUNTAS FRECUENTES
        </h2>
      </div>

      <div
        className="absolute inset-0 mt-20 bg-[radial-gradient(ellipse_80%_80%_at_center,#D08E00_8%,#6A4800_32%,#000000_50%)]
                  md:bg-[radial-gradient(ellipse_60%_100%_at_center,#D08E00_8%,#6A4800_32%,#000000_50%)]
                  opacity-30 pointer-events-none z-30 animate-pulseScale"
      />

      <div className="max-w-6xl mx-auto space-y-8 relative z-40">
        {rows.map((row, rowIdx) => {
          const leftIsLarge = rowIdx % 2 === 1;
          const rightIsLarge = rowIdx % 2 === 0;

          const leftW = leftIsLarge ? "md:w-[55%]" : "md:w-[45%]";
          const rightW = rightIsLarge ? "md:w-[55%]" : "md:w-[45%]";

          // ✅ Direcciones alternadas por fila (rowIdx)
          // Fila 1 (rowIdx 0): izquierda RL, derecha LR
          // Fila 2 (rowIdx 1): izquierda LR, derecha RL
          // ...
          const leftBorderDir: "lr" | "rl" = rowIdx % 2 === 0 ? "rl" : "lr";
          const rightBorderDir: "lr" | "rl" = rowIdx % 2 === 0 ? "lr" : "rl";

          return (
            <div key={rowIdx} className="flex flex-col md:flex-row gap-6">
              <FaqCard
                idx={row.left}
                wClass={leftW}
                isLarge={leftIsLarge}
                borderDir={leftBorderDir}
              />

              {row.right !== undefined && (
                <FaqCard
                  idx={row.right}
                  wClass={rightW}
                  isLarge={rightIsLarge}
                  borderDir={rightBorderDir}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* MODAL */}
      {activeFaq && open && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="FAQ"
        >
          {/* Backdrop clickable */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onMouseDown={closeFaq}
          />

          {/* WRAPPER */}
          <div
            className="relative w-full max-w-3xl rounded-2xl p-[1px] shadow-2xl"
            style={{
              background: borderLR,
              maxHeight: "56vh",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Panel */}
            <div
              className={`w-full h-full overflow-hidden rounded-2xl
                ${
                  open.isLarge
                    ? "bg-gradient-to-r from-[#000000] to-[#201600]"
                    : "bg-gradient-to-r from-[#201600] to-[#000000]"
                }`}
              style={{ maxHeight: "56vh" }}
            >
              <div className="flex items-start justify-between gap-4 p-6">
                <div className="min-w-0">
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold leading-snug">
                    {activeFaq.question}
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={closeFaq}
                  className="shrink-0 rounded-full p-2 hover:bg-white/10 transition"
                  aria-label="Cerrar"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="px-6 pb-6 overflow-auto">
                <p className="text-gray-200 leading-relaxed whitespace-pre-line text-sm sm:text-base">
                  {activeFaq.answer}
                </p>
              </div>
            </div>
          </div>

          <KeyListener onEscape={closeFaq} />
        </div>
      )}

      <style>{`
        @keyframes pulseScale {
          0%, 100% { transform: scale(1); opacity: 0.28; }
          50% { transform: scale(1.03); opacity: 0.35; }
        }
        .animate-pulseScale { animation: pulseScale 4.5s ease-in-out infinite; }
      `}</style>
    </section>
  );
};

const KeyListener: React.FC<{ onEscape: () => void }> = ({ onEscape }) => {
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onEscape();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onEscape]);
  return null;
};

export default FAQSection;
